import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {
  MoveToInbox as MoveToInboxIcon,
  ThumbUp as ThumbUpIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  DeleteForever as DeleteForeverIcon,
  HideSourceSharp as HideSourceIcon,
  ContentCut as ContentCutIcon,
  CallMerge as MergeIcon
} from '@mui/icons-material';
import { ClassNameMap } from '@mui/styles';
import { useFormContext, useWatch } from 'react-hook-form';
import { uniq } from 'lodash';
import { NotesClassification } from 'modules/notes/NotesFilterContextProvider';
import { useMessages } from 'modules/intl';
import { NotesActions } from '../hooks';
import { TimeSignalAction } from './TimeSignalAction';

export type BulkActions = NotesActions | 'split' | 'multi-edit' | 'merge';

const useStyles = makeStyles((theme) => ({
  actions: {
    fontSize: theme.typography.body1.fontSize,
    margin: theme.spacing(0, 1, 0, 1),
    textTransform: 'none'
  },
  icons: {
    padding: theme.spacing(1, 1)
  },
  rotateCut: {
    transform: 'rotate(-90deg)'
  }
}));

type TimeSignalActionsByCategoryProps = {
  category: NotesClassification;
  actionCallback: (classification: BulkActions) => void;
  selectedCount: number;
  loading: boolean;
  color?: 'default' | 'inherit' | 'primary' | 'secondary';
  iconClasses?: ClassNameMap;
};

export const MERGE_NOTES_LIMIT = 50;

const messageIds = [
  'timeSignalActions.inbox',
  'timeSignalActions.private',
  'timeSignalActions.accept',
  'timeSignalActions.trash',
  'timeSignalActions.deleteForever',
  'timeSignalActions.splitNote',
  'timeSignalActions.edit',
  'timeSignalActions.merge'
];
const TimeSignalActionsByCategory: React.FC<TimeSignalActionsByCategoryProps> = ({
  category,
  actionCallback,
  selectedCount,
  loading,
  color = 'primary',
  iconClasses
}: TimeSignalActionsByCategoryProps) => {
  const { control } = useFormContext();
  const values = useWatch({ control });
  const daysOfSelectedNotes = useMemo(() => {
    const days = Object.keys(values)
      .filter((key) => {
        const items = key.split('_');
        return items && items.length === 2 && values[key];
      })
      .map((key) => {
        const items = key.split('_');
        return items[0];
      });

    return uniq(days);
  }, [values]);

  const classes = useStyles();
  const messages = useMessages(messageIds);

  const timeSignalActions = useMemo(
    () =>
      [
        {
          testId: 'multi-edit-button',
          show:
            daysOfSelectedNotes &&
            daysOfSelectedNotes.length === 1 &&
            category !== 'Trash',
          handleClick: () => actionCallback('multi-edit'),
          displayText: messages['timeSignalActions.edit'],
          icon: <EditIcon />
        },
        {
          testId: 'inbox-button',
          show: category === 'Unclassified' ? false : true,
          handleClick: () => actionCallback('Unclassified'),
          displayText: messages['timeSignalActions.inbox'],
          icon: <MoveToInboxIcon />
        },
        {
          testId: 'private-button',
          show: category === 'Private' ? false : true,
          handleClick: () => actionCallback('Private'),
          displayText: messages['timeSignalActions.private'],
          icon: <HideSourceIcon />
        },
        {
          testId: 'accept-button',
          show: category === 'Accepted' ? false : true,
          handleClick: () => actionCallback('Accepted'),
          displayText: messages['timeSignalActions.accept'],
          icon: <ThumbUpIcon />
        },
        {
          testId: 'trash-button',
          show: category === 'Trash' ? false : true,
          handleClick: () => actionCallback('Trash'),
          displayText: messages['timeSignalActions.trash'],
          icon: <DeleteIcon />
        },
        {
          testId: 'delete-forever-button',
          show: category === 'Trash' ? true : false,
          handleClick: () => actionCallback('DeleteForever'),
          displayText: messages['timeSignalActions.deleteForever'],
          icon: <DeleteForeverIcon />
        },
        {
          testId: 'merge-button',
          show:
            daysOfSelectedNotes &&
            daysOfSelectedNotes.length === 1 &&
            category !== 'Trash' &&
            selectedCount > 1,
          handleClick: () => actionCallback('merge'),
          displayText: messages['timeSignalActions.merge'],
          icon: <MergeIcon />,
          disabled: selectedCount > MERGE_NOTES_LIMIT
        },
        {
          testId: 'split-button',
          show: category !== 'Trash' && selectedCount === 1,
          handleClick: () => actionCallback('split'),
          displayText: messages['timeSignalActions.splitNote'],
          icon: <ContentCutIcon className={classes.rotateCut} />,
          disabled: selectedCount !== 1
        }
      ].filter((action) => action.show),
    [
      messages,
      category,
      classes.rotateCut,
      selectedCount,
      actionCallback,
      daysOfSelectedNotes
    ]
  );

  return (
    <Box data-testid="time-signal-actions" hidden={!selectedCount}>
      {timeSignalActions.map((action) => (
        <TimeSignalAction
          key={action.testId}
          testId={action.testId}
          title={action.displayText}
          className={classes.icons}
          icon={action.icon}
          onClick={action.handleClick}
          loading={loading}
          color={color}
          classes={iconClasses}
          disabled={action.disabled}
        />
      ))}
    </Box>
  );
};
export default React.memo(TimeSignalActionsByCategory);
